import {
    Card,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@repo/shadcn';
import { Loader2Icon } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { TextField } from '@components/form/fields';

import { formatCurrencyInBDT } from '@/utils';

import { useCreatePurchaseReturnContext } from './create-purchase-return.context';
import { TCreatePurchaseReturnForm } from './create-purchase-return.schema';

export const PurchaseItems = () => {
    const {
        data,
        isPurchaseDetailsLoading,
        isPurchaseDetailsFetching,
        isPurchaseDetailsError,
    } = useCreatePurchaseReturnContext();

    const { watch, control } = useFormContext<TCreatePurchaseReturnForm>();

    const items = watch('items');

    const { update } = useFieldArray({
        control,
        name: 'items',
    });

    if (isPurchaseDetailsFetching || isPurchaseDetailsLoading) {
        return (
            <Card
                className={
                    'flex flex-col items-center justify-center gap-2 px-4 py-8'
                }
            >
                <Loader2Icon className={'animate-spin'} />
            </Card>
        );
    }

    if (isPurchaseDetailsError) {
        return (
            <Card className={'p-4 text-red-600'}>
                Error loading purchase details.
            </Card>
        );
    }

    if (data && data?.items?.length === 0) {
        return <Card className={'p-4'}>No items found in this purchase.</Card>;
    }

    return (
        <Card className={'p-4'}>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[50px]"></TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead className={'text-right'}>Price</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead className="text-right">
                            Return Quantity
                        </TableHead>
                        <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {items?.map((item, index) => (
                        <TableRow key={item.purchase_item_id}>
                            <TableCell>
                                <Checkbox
                                    onCheckedChange={(checkedState) => {
                                        update(index, {
                                            ...item,
                                            is_selected: Boolean(checkedState),
                                        });
                                    }}
                                />
                            </TableCell>

                            <TableCell>{item.purchase_item_name}</TableCell>

                            <TableCell className={'text-right'}>
                                {formatCurrencyInBDT(item.price)}
                            </TableCell>

                            <TableCell className={'text-right'}>
                                {item.quantity}
                            </TableCell>

                            <TableCell className="text-right">
                                <TextField<TCreatePurchaseReturnForm>
                                    inputClass={'text-right'}
                                    disabled={!item.is_selected}
                                    type={'number'}
                                    inputProps={{
                                        min: 1,
                                        max: item.quantity,
                                        step: 1,
                                    }}
                                    name={`items.${index}.return_quantity`}
                                />
                            </TableCell>

                            <TableCell className={'text-right'}>
                                {formatCurrencyInBDT(
                                    item.price * item.return_quantity,
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Card>
    );
};

PurchaseItems.displayName = 'PurchaseItems';
